import { Component, Inject, OnInit, HostListener, ViewChild, ElementRef, AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslocoService } from '@ngneat/transloco';

import { Compbaser } from '../compbaser';
import { PaletteService, ColorPalette } from '../../services/palette.service';

interface CategoryItem {
  name: string;
  count: number;
}

export interface DialogPaletteSelectorData {
  selectedPalette?: ColorPalette;
}

export interface DialogPaletteSelectorResult {
  palette: ColorPalette;
}

@Component({
  selector: 'app-dialog-palette-selector',
  templateUrl: './dialog-palette-selector.component.html',
  styleUrls: ['./dialog-palette-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DialogPaletteSelectorComponent extends Compbaser implements OnInit, AfterViewInit {
  selectedCategory: string | null = null;
  selectedPalette: ColorPalette | null = null;
  filteredPalettes: ColorPalette[] = [];
  allPalettes: ColorPalette[] = [];
  categoryList: CategoryItem[] = [];
  filteredCategoryList: CategoryItem[] = [];
  searchQuery = '';
  isMobileView = false;
  hoveredColorType: string | null = null;
  clickedColorType: string | null = null;
  showPreview = false;

  // Resize splitter properties
  isResizing = false;
  gridHeight = 60; // Default 60% for grid, 40% for preview
  previewHeight = 40;

  @ViewChild('palettesGrid') palettesGrid: ElementRef;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogPaletteSelectorData,
    private dialogRef: MatDialogRef<DialogPaletteSelectorComponent>,
    public override dialog: MatDialog,
    private paletteService: PaletteService,
    private translocoService: TranslocoService,
    private cd: ChangeDetectorRef
  ) {
    super(dialog);
    if (data?.selectedPalette) {
      this.selectedPalette = data.selectedPalette;
    }
  }

  ngOnInit(): void {
    this.allPalettes = this.paletteService.getPalettes();
    this.buildCategoryList();

    // Load previously selected palette from localStorage first
    this.loadSavedPalette();

    // Load saved split position from localStorage
    this.loadSavedSplitPosition();

    // Show all palettes by default (don't auto-select)
    this.selectCategory(null);

    // Check initial mobile view state
    this.checkMobileView();
  }

  ngAfterViewInit(): void {
    // Scroll to selected palette if showing all palettes
    if (this.selectedCategory === null && this.selectedPalette) {
      setTimeout(() => this.scrollToSelectedPalette(), 100);
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this.checkMobileView();
  }

  private checkMobileView(): void {
    const wasMobile = this.isMobileView;
    this.isMobileView = window.innerWidth < 1260;
    if (wasMobile !== this.isMobileView) {
      this.cd.markForCheck();
    }
  }

  private buildCategoryList(): void {
    const categories = this.paletteService.getCategoryNames();
    const grouped = this.paletteService.getPalettesByCategory() as { [category: string]: ColorPalette[] };

    this.categoryList = categories.map(category => ({
      name: category,
      count: grouped[category]?.length || 0
    }));

    // Initially show all categories
    this.filteredCategoryList = [...this.categoryList];
  }

  selectCategory(category: string | null): void {
    this.selectedCategory = category;
    this.applyFilters();

    if (category === null) {
      // "All Palettes" selected - don't show preview
      this.showPreview = false;
      // Keep current selection if exists (from localStorage or previous selection)
      // If no previous selection exists, selectedPalette remains null
    } else {
      // Specific category selected - show preview and auto-select first palette
      this.showPreview = true;
      // Auto-select first palette in this category
      if (this.filteredPalettes.length > 0) {
        this.selectedPalette = this.filteredPalettes[0];
      }
    }
    this.cd.markForCheck();
  }

  selectPalette(palette: ColorPalette): void {
    this.selectedPalette = palette;
    // When user clicks a palette card, show preview
    this.showPreview = true;
    this.cd.markForCheck();
  }

  applyFilters(): void {
    let palettes = this.allPalettes;

    // Filter by search query first
    if (this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase();
      palettes = palettes.filter(p =>
        p.name.toLowerCase().includes(query) ||
        p.category.toLowerCase().includes(query)
      );

      // Filter categories based on search results
      const matchingCategories = new Set(palettes.map(p => p.category));
      this.filteredCategoryList = this.categoryList
        .map(cat => {
          const matchingCount = palettes.filter(p => p.category === cat.name).length;
          return {
            name: cat.name,
            count: matchingCount
          };
        })
        .filter(cat => cat.count > 0);
    } else {
      // No search query - show all categories
      this.filteredCategoryList = [...this.categoryList];
    }

    // Filter by selected category
    if (this.selectedCategory) {
      palettes = palettes.filter(p => p.category === this.selectedCategory);
    }

    this.filteredPalettes = palettes;
    this.cd.markForCheck();
  }

  onSearchChange(): void {
    // Clear category selection when searching to show all matching results
    if (this.searchQuery.trim() && this.selectedCategory) {
      this.selectedCategory = null;
    }
    this.applyFilters();
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.applyFilters();
  }

  applyPalette(): void {
    if (this.selectedPalette) {
      // Save the selected palette using centralized service
      this.paletteService.setCurrentPalette(this.selectedPalette);
      this.dialogRef.close({ palette: this.selectedPalette });
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  getRandomPalette(): void {
    this.selectedPalette = this.paletteService.getRandomPalette();
    this.showPreview = true;
    this.cd.markForCheck();
  }

  /**
   * Load previously saved palette from centralized service and select it
   */
  private loadSavedPalette(): void {
    const savedPalette = this.paletteService.getCurrentPalette();
    if (savedPalette) {
      this.selectedPalette = savedPalette;
      console.log(`[palette-selector] Loaded saved palette: ${savedPalette.name}`);
    }
  }

  /**
   * Load saved split position from localStorage
   */
  private loadSavedSplitPosition(): void {
    const savedSplit = localStorage.getItem('palette-selector-split');
    if (savedSplit) {
      try {
        const split = JSON.parse(savedSplit);
        this.gridHeight = split.gridHeight || 60;
        this.previewHeight = split.previewHeight || 40;
      } catch (e) {
        console.warn('Failed to load split position:', e);
      }
    }
  }

  /**
   * Set the hovered color type for preview highlighting
   */
  setHoveredColor(colorType: string): void {
    this.hoveredColorType = colorType;
  }

  /**
   * Clear the hovered color type
   */
  clearHoveredColor(): void {
    this.hoveredColorType = null;
  }

  /**
   * Handle hover on color item to pulse the corresponding preview element(s)
   * Now triggers on mouseenter, clears on mouseleave for instant feedback
   */
  onColorClick(colorType: string): void {
    this.clickedColorType = colorType;
  }

  /**
   * Scroll to the selected palette card in the grid
   */
  private scrollToSelectedPalette(): void {
    if (!this.selectedPalette || !this.palettesGrid) return;

    const gridElement = this.palettesGrid.nativeElement;
    const selectedCard = gridElement.querySelector('.palette-card.selected');

    if (selectedCard) {
      selectedCard.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center'
      });
    }
  }

  /**
   * Start resizing when user clicks on the resize handle
   */
  onResizeStart(event: MouseEvent): void {
    event.preventDefault();
    this.isResizing = true;

    // Add global mouse move and mouse up listeners
    const onMouseMove = (e: MouseEvent) => this.onResizeMove(e);
    const onMouseUp = () => this.onResizeEnd(onMouseMove, onMouseUp);

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    // Add cursor style to body during resize
    document.body.style.cursor = 'row-resize';
  }

  /**
   * Handle mouse move during resize
   */
  private onResizeMove(event: MouseEvent): void {
    if (!this.isResizing) return;

    // Get the main-content container
    const mainContent = document.querySelector('.main-content') as HTMLElement;
    if (!mainContent) return;

    // Calculate the new split position as percentage
    const rect = mainContent.getBoundingClientRect();
    const mouseY = event.clientY - rect.top;
    const containerHeight = rect.height;

    // Calculate percentage (with limits: 15% min for grid, 15% min for preview)
    let newGridPercent = (mouseY / containerHeight) * 100;
    newGridPercent = Math.max(15, Math.min(85, newGridPercent)); // Clamp between 15-85%

    this.gridHeight = newGridPercent;
    this.previewHeight = 100 - newGridPercent;

    this.cd.markForCheck();
  }

  /**
   * End resizing when user releases mouse
   */
  private onResizeEnd(moveHandler: (e: MouseEvent) => void, upHandler: () => void): void {
    this.isResizing = false;

    // Remove global listeners
    document.removeEventListener('mousemove', moveHandler);
    document.removeEventListener('mouseup', upHandler);

    // Restore cursor
    document.body.style.cursor = '';

    // Save the split position to localStorage
    localStorage.setItem('palette-selector-split', JSON.stringify({
      gridHeight: this.gridHeight,
      previewHeight: this.previewHeight
    }));

    this.cd.markForCheck();
  }
}
